import { useEffect, useState } from 'react';

import type { ConnectingDelayJudgementMode, ConnectingDirectionEntry } from '../../../api/types';
import type { RouteMaster } from '../../../data/routes';
import type { RouteStop } from '../../../data/routeStops';
import { createDefaultConnectingStationDirection } from '../../../utils/broadcastContents';
import type { RevealTarget, ValidationIssue } from '../../../utils/validationIssues';
import { ConnectingDirectionBlock } from './ConnectingDirectionBlock';

/**
 * 接続線遅延 (乗換駅ベース / part2) の 1 接続線ブロック内の「方面リスト」セクション。
 *
 * 方面は 1〜2 件に制限し、＋ボタン (最大2件)・−ボタン (最後の1件は残す)・↑↓ ボタン
 * (順序変更) を提供する。方面ごとの詳細入力は `ConnectingDirectionBlock` に委譲する。
 */
export function ConnectingDirectionsSection({
  lineIndex,
  routeId,
  routeIds = [],
  judgementMode,

  routes,
  routeStops,
  directions,
  issues = [],
  revealTarget = null,
  languages = ['ja'],
  onChangeDirections,
}: {
  /** コンテンツの放送言語 (方面名の言語別入力欄の表示に使う / モンキーテスト B-1)。 */
  languages?: readonly string[];
  /** 親接続線ブロックの index (data-testid 用) */
  lineIndex: number;
  /**
   * この接続線に紐づく入力エラー (親で `lineIndex` 絞り込み済み)。
   * 方面タブの ⚠ マークと、方面名・区間駅の赤枠に使う。
   */
  issues?: ValidationIssue[];
  /**
   * 方面タブを自動で開くための指示。
   * 方面はタブ表示で選択中の 1 件しか描画しないため、指示に従ってタブを
   * 切り替えないと該当入力欄が DOM に存在せずスクロールできない。
   */
  revealTarget?: RevealTarget | null;

  /** 親接続線の route_id (方面の direction_id / 区間駅の選択肢に使う) */
  routeId: string;
  /**
   * 親接続線の「遅延を検索する線区」(`route_id` + `route_ids`)。
   * 方面ブロックの「遅延を判定する向き」表 (`route_directions`) の行に使う。
   */
  routeIds?: readonly string[];
  /** 親接続線の判定モード (項目表示切替に使う) */

  judgementMode: ConnectingDelayJudgementMode;
  routes: RouteMaster[];
  routeStops: Record<string, RouteStop[]>;
  directions: ConnectingDirectionEntry[];
  onChangeDirections: (next: ConnectingDirectionEntry[]) => void;
}): React.JSX.Element {
  // タブ表示: 選択中の方面 index。方面が 2 件あるとき横並びタブで切り替える。
  const [activeDir, setActiveDir] = useState<number>(0);
  const activeDirIndex = Math.min(activeDir, Math.max(0, directions.length - 1));

  /*
   * エラーサマリから「該当方面を開く」指示が来たらタブを切り替える。
   *
   * 方面はタブ表示で選択中の 1 件しか本文を描画しないため、タブを切り替えないと
   * 該当入力欄が DOM に存在せずスクロール・フォーカスができない。
   * `nonce` を依存配列に含めるのは「同じ方面をもう一度開く」ケースのため
   * (詳細は `RevealTarget` の JSDoc)。
   *
   * なお `lineIndex` が一致しない指示 (別の接続線のエラー) は無視する。
   * 接続線タブ側の切替で本コンポーネント自体が入れ替わるため、
   * 他線のエラーで方面タブまで動かす必要はない。
   */
  useEffect(() => {
    if (revealTarget?.directionIndex === undefined) return;
    if (revealTarget.lineIndex !== undefined && revealTarget.lineIndex !== lineIndex) return;
    setActiveDir(revealTarget.directionIndex);
    // nonce は「同じ index でも再実行させる」ためだけに依存配列へ含める。
  }, [revealTarget?.directionIndex, revealTarget?.lineIndex, revealTarget?.nonce, lineIndex]);

  /**
   * 指定した方面にエラーがあるか (タブの ⚠ マーク判定)。
   *
   * `issues` は親でこの接続線ぶんに絞り込まれているので `directionIndex` のみ見る。
   * 「タブを開かないとエラーの有無すら分からない」状態を避けるのが目的。
   */
  const directionHasIssue = (directionIndex: number): boolean =>
    issues.some((i) => i.target.directionIndex === directionIndex);

  /** index 番目の方面を更新 */
  const updateDirection = (index: number, next: ConnectingDirectionEntry): void => {
    const nextDirections = directions.slice();
    nextDirections[index] = next;
    onChangeDirections(nextDirections);
  };
  /**
   * 方面を末尾に追加 (最大 2 件まで)。
   *
   * 方面はタブ表示で選択中の 1 件ぶんしか本文を描画しないため、追加直後に
   * 選択タブを新しく追加した方面 (= 追加後の末尾 index) へ切り替える。
   * こうしないと「追加したのに画面が変わらない」状態になり、ユーザが
   * 追加した方面の入力欄にたどり着けない (Issue #356)。
   * 追加後の末尾 index は「追加前の件数」= `directions.length` となる。
   */
  const addDirection = (): void => {
    if (directions.length >= 2) return;
    setActiveDir(directions.length);
    onChangeDirections([...directions, createDefaultConnectingStationDirection()]);
  };
  /** 方面を削除 (最後の 1 件は残す) */
  const removeDirection = (index: number): void => {
    if (directions.length <= 1) return;
    const nextDirections = directions.slice();
    nextDirections.splice(index, 1);
    onChangeDirections(nextDirections);
  };
  /** 方面を上へ移動 */
  const moveUp = (index: number): void => {
    if (index <= 0) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index - 1, 0, moved);
    onChangeDirections(nextDirections);
  };
  /** 方面を下へ移動 */
  const moveDown = (index: number): void => {
    if (index >= directions.length - 1) return;
    const nextDirections = directions.slice();
    const [moved] = nextDirections.splice(index, 1);
    if (!moved) return;
    nextDirections.splice(index + 1, 0, moved);
    onChangeDirections(nextDirections);
  };

  const canAdd = directions.length < 2;
  const canRemove = directions.length > 1;

  return (
    <div
      className="connecting-directions-section"
      data-testid={`connecting-directions-section-${lineIndex}`}
      style={{ marginTop: 8 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
        <strong>方面 ({directions.length}/2 件)</strong>
        {canAdd ? (
          <button
            type="button"
            data-testid={`connecting-direction-add-${lineIndex}`}
            aria-label={`接続線#${lineIndex + 1} に方面を追加`}
            onClick={addDirection}
          >
            ＋ 方面を追加
          </button>
        ) : (
          <span className="hint">最大 2 件まで追加できます</span>
        )}
      </div>
      {/* 方面タブ (横並び)。選択中の方面のみ本文を表示する。 */}
      {directions.length > 0 ? (
        <div
          className="connecting-direction-tabs"
          role="tablist"
          data-testid={`connecting-direction-tabs-${lineIndex}`}
        >
          {directions.map((direction, di) => {
            const isActive = di === activeDirIndex;
            const label = (direction.direction_name ?? '').trim() || `方面#${di + 1}`;
            return (
              <button
                key={di}
                type="button"
                role="tab"
                aria-selected={isActive}
                data-testid={`connecting-direction-tab-${lineIndex}-${di}`}
                className={`connecting-direction-tab${isActive ? ' is-active' : ''}`}
                onClick={() => setActiveDir(di)}
              >
                #{di + 1} {label}
                {/*
                  この方面にエラーがあることをタブ上で示す。
                  タブを開かないとエラーの有無すら分からない状態を避けるための表示で、
                  クリックすればそのままタブが切り替わって該当欄を確認できる。
                */}
                {directionHasIssue(di) ? (
                  <span
                    className="connecting-direction-tab__error"
                    data-testid={`connecting-direction-tab-error-${lineIndex}-${di}`}
                    aria-label={`方面#${di + 1} に入力エラーあり`}
                  >
                    {' '}
                    ⚠
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
      <ol
        className="connecting-directions-list"
        style={{ listStyle: 'none', padding: 0, margin: 0 }}
      >
        {directions.map((direction, di) =>
          di === activeDirIndex ? (
            <li
              key={di}
              className="connecting-direction-item"
              data-testid={`connecting-direction-item-${lineIndex}-${di}`}
              style={{
                border: '1px dashed #ccc',
                borderRadius: 4,
                padding: '6px 10px',
                marginTop: 6,
              }}
            >
              <ConnectingDirectionBlock
                lineIndex={lineIndex}
                directionIndex={di}
                direction={direction}
                routeId={routeId}
                routeIds={routeIds}
                judgementMode={judgementMode}
                routes={routes}
                routeStops={routeStops}
                // この方面ぶんに絞ったエラー (方面名 / 区間駅の赤枠に使う)。
                issues={issues.filter((issue) => issue.target.directionIndex === di)}
                languages={languages}
                canRemove={canRemove}
                isFirst={di === 0}
                isLast={di === directions.length - 1}
                onChange={(next) => updateDirection(di, next)}
                onRemove={() => {
                  removeDirection(di);
                  setActiveDir(Math.max(0, di - 1));
                }}
                onMoveUp={() => {
                  moveUp(di);
                  setActiveDir(Math.max(0, di - 1));
                }}
                onMoveDown={() => {
                  moveDown(di);
                  setActiveDir(Math.min(directions.length - 1, di + 1));
                }}
              />
            </li>
          ) : null,
        )}
      </ol>
    </div>
  );
}
